import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Smartphone, Tablet, X } from 'lucide-react';
import { istAndroidBrowser, type AppVariante } from '../../services/nativePlatform';
import { geraeteVorschlag, ladeSitzungsStand, VARIANTEN_TEXT } from '../../services/appRelease';

/**
 * Dezenter Hinweis „Die App gibt es auch für Android" — NUR im Android-
 * BROWSER (nicht in der App, nicht auf iPhone/iPad, nicht am Rechner), nur
 * fuer angemeldete interne Nutzer.
 *
 * Eingeschaltet wird er vom Release (Entscheid nach dem Pilot): er erscheint
 * nur, wenn die Release-Info der fuer DIESES Geraet vorgeschlagenen Variante
 * (kuerzere Bildschirmseite ab 600 CSS-px → Tablet) `"hinweis": true` traegt
 * (Release-Skript `--hinweis`). Vorgabe ist aus — im Pilot erscheint nichts.
 * Einmal weggeklickt, bleibt er weg.
 *
 * Eigene Leiste auf App-Ebene (Vertrag E6), im Fluss oben in <main>; die
 * Dashboard-Dateien bleiben unberuehrt.
 *
 * Der Merker liegt im localStorage, gekapselt in try/catch. Ohne Speicher
 * (privater Modus, gesperrt) rendert der Hinweis trotzdem und laesst sich
 * wegklicken — dann nur bis zum Neuladen.
 */

const WEGGEKLICKT_SCHLUESSEL = 'appDownloadHinweis.weggeklickt';

const leseWeggeklickt = (): boolean => {
    try {
        return window.localStorage.getItem(WEGGEKLICKT_SCHLUESSEL) === '1';
    } catch {
        return false;
    }
};

const merkeWeggeklickt = (): void => {
    try {
        window.localStorage.setItem(WEGGEKLICKT_SCHLUESSEL, '1');
    } catch {
        // ohne Speicher gilt das Wegklicken bis zum Neuladen
    }
};

export function AppDownloadHinweis({ onOpen }: { onOpen: () => void }): JSX.Element | null {
    const [android] = useState<boolean>(() => istAndroidBrowser());
    const [variante] = useState<AppVariante>(() => geraeteVorschlag());
    const [weggeklickt, setWeggeklickt] = useState<boolean>(() => (android ? leseWeggeklickt() : true));
    const [bereit, setBereit] = useState(false);

    useEffect(() => {
        if (!android || weggeklickt) return undefined;
        let aktiv = true;
        // Einmal je Anmeldung zwischengespeichert (ladeSitzungsStand): die
        // Leiste wird bei jedem Projekt-Oeffnen neu eingehaengt, der Abruf
        // (Profil, signierte URLs, JSON) laeuft trotzdem nur einmal.
        void ladeSitzungsStand().then(({ zugang, releases }) => {
            if (aktiv && zugang.zugang === 'intern' && releases[variante]?.hinweis === true) setBereit(true);
        });
        return () => { aktiv = false; };
    }, [android, weggeklickt, variante]);

    if (!android || weggeklickt || !bereit) return null;

    const VariantenIcon = variante === 'tablet' ? Tablet : Smartphone;

    return (
        <div className="shrink-0 bg-emerald-50 border-b border-emerald-200 text-emerald-900" role="status">
            <div className="px-4 py-2 flex items-center gap-3 text-sm">
                <VariantenIcon className="w-4 h-4 shrink-0 text-emerald-600" />
                <span className="flex-1 min-w-0">Die Web-App gibt es auch als {VARIANTEN_TEXT[variante].titel} für Android.</span>
                <button
                    type="button"
                    onClick={() => {
                        // Ueber der Download-Seite waere die Leiste doppelt; sie
                        // verschwindet bis zum Neuladen, gemerkt wird nur das ✕.
                        setWeggeklickt(true);
                        onOpen();
                    }}
                    className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1 rounded-lg text-xs"
                >
                    Ansehen
                </button>
                <button
                    type="button"
                    onClick={() => {
                        merkeWeggeklickt();
                        setWeggeklickt(true);
                    }}
                    aria-label="Hinweis ausblenden"
                    className="shrink-0 p-1 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
